import { useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import Switch from 'react-switch'
import { useI18n } from '../../../provider/i18n'
import { Green600 } from '../../../lib/colors'
import useBasketData from '../../../hooks/useBasketData'

const Consents = () => {
  const i18n = useI18n()
  const [showMore, setShowMore] = useState(false)
  const { shippingTests, form } = useBasketData()
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = form

  const isShippingTestExists = useMemo(
    () => !!shippingTests.length,
    [shippingTests]
  )

  const toggleShowMore = () => setShowMore((value) => !value)

  const [c1, c3, c4, sc1, sc2] = watch([
    'consent1',
    'consent3',
    'consent4',
    'shippingConsent1',
    'shippingConsent2',
  ])

  const allRequiredChecked = isShippingTestExists
    ? [c1, c3, c4, sc1, sc2].every(Boolean)
    : [c1, c3, c4].every(Boolean)

  const handleSelectAll = (checked: boolean) => {
    setValue('consent1', checked)
    setValue('consent3', checked)
    setValue('consent4', checked)
    if (isShippingTestExists) {
      setValue('shippingConsent1', checked)
      setValue('shippingConsent2', checked)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-lg md:text-xl font-bold">
        {i18n.t('screens.basket.sections.consents.title')}
      </span>
      <div
        className={
          'flex flex-col gap-4 bg-white border-b border-b-border border-opacity-20 p-2 md:p-4 rounded-lg'
        }
      >
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch
            checked={allRequiredChecked}
            onChange={handleSelectAll}
            uncheckedIcon={false}
            checkedIcon={false}
            width={40}
            height={24}
            onColor={Green600}
          />
          <span className="text-xs font-bold text-blue-700">
            {i18n.t('screens.basket.form.selectAllRequired')}
          </span>
        </label>
        <Controller
          control={control}
          rules={{
            required: i18n.t('screens.basket.form.consent.rules.required'),
          }}
          name="consent1"
          render={({ field: { value, onChange, ref } }) => (
            <label className="flex items-center gap-2 cursor-pointer" ref={ref}>
              <Switch
                checked={value}
                onChange={onChange}
                uncheckedIcon={false}
                checkedIcon={false}
                width={40}
                height={24}
                onColor={Green600}
              />
              <span className="text-xs text-blue-700">
                {i18n.t('common.order.consent.text1')}
                {errors.consent1 && (
                  <p className="hl-input-error">{errors.consent1.message}</p>
                )}
              </span>
            </label>
          )}
        />
        <Controller
          control={control}
          rules={{
            required: i18n.t('screens.basket.form.consent.rules.required'),
          }}
          name="consent3"
          render={({ field: { value, onChange, ref } }) => (
            <label className="flex gap-2 cursor-pointer" ref={ref}>
              <Switch
                checked={value}
                onChange={onChange}
                uncheckedIcon={false}
                checkedIcon={false}
                width={40}
                height={24}
                onColor={Green600}
              />
              <div className="text-xs text-blue-700">
                <span className="leading-tight">
                  {i18n.t('common.order.consent.text3.less')}
                </span>
                {showMore ? (
                  <>
                    <span className="leading-tight">
                      {i18n.t('common.order.consent.text3.more')}
                    </span>
                    <br />
                    <span className="leading-tight whitespace-pre-wrap">
                      {i18n.t('common.order.consent.text3.bullets')}
                    </span>
                  </>
                ) : (
                  '...'
                )}{' '}
                <a
                  className="font-bold underline"
                  onClick={(e) => {
                    e.preventDefault()
                    toggleShowMore()
                  }}
                >
                  {i18n.t(showMore ? 'common.less' : 'common.more')}
                </a>
                {errors.consent3 && (
                  <p className="hl-input-error">{errors.consent3.message}</p>
                )}
              </div>
            </label>
          )}
        />
        <Controller
          control={control}
          rules={{
            required: i18n.t('screens.basket.form.consent.rules.required'),
          }}
          name="consent4"
          render={({ field: { value, onChange, ref } }) => (
            <label className="flex items-center gap-2 cursor-pointer" ref={ref}>
              <Switch
                checked={value}
                onChange={onChange}
                uncheckedIcon={false}
                checkedIcon={false}
                width={40}
                height={24}
                onColor={Green600}
              />
              <span className="text-xs text-blue-700">
                {i18n.t('common.order.consent.text4')}
                {errors.consent4 && (
                  <p className="hl-input-error">{errors.consent4.message}</p>
                )}
              </span>
            </label>
          )}
        />
        {isShippingTestExists && (
          <>
            <Controller
              control={control}
              rules={{
                required: i18n.t('screens.basket.form.consent.rules.required'),
              }}
              shouldUnregister
              name="shippingConsent1"
              render={({ field: { value, onChange, ref } }) => (
                <label className="flex items-center gap-2 cursor-pointer" ref={ref}>
                  <Switch
                    checked={value}
                    onChange={onChange}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    width={40}
                    height={24}
                    onColor={Green600}
                  />
                  <span className="text-xs text-blue-700">
                    {i18n.t('common.order.shippingConsent.text1')}{' '}
                    {i18n.t('common.order.shippingConsent.text3')}
                    {errors.shippingConsent1 && (
                      <p className="hl-input-error">{errors.shippingConsent1.message}</p>
                    )}
                  </span>
                </label>
              )}
            />
            <Controller
              control={control}
              rules={{
                required: i18n.t('screens.basket.form.consent.rules.required'),
              }}
              shouldUnregister
              name="shippingConsent2"
              render={({ field: { value, onChange, ref } }) => (
                <label className="flex items-center gap-2 cursor-pointer" ref={ref}>
                  <Switch
                    checked={value}
                    onChange={onChange}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    width={40}
                    height={24}
                    onColor={Green600}
                  />
                  <span className="text-xs text-blue-700">
                    {i18n.t('common.order.shippingConsent.text2')}
                    {errors.shippingConsent2 && (
                      <p className="hl-input-error">{errors.shippingConsent2.message}</p>
                    )}
                  </span>
                </label>
              )}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default Consents
